import React, {useEffect, useReducer, useRef} from 'react'
import splitAddress from '../../utils/splitAddress.js'
import formatAddress from '../../../server/utils/formatAddress.js'
import {Colors, IconButton, P, Popover, Row, Card, ModalHeader} from '../UI/index.js'
import {formSpacing, Select, Toggle} from '../UI/forms/index.js'

// Fraction of changed thumbnail pixels above which a new parcel/scene is detected
const SCENE_CHANGE_THRESHOLD = 0.01

// Address found with the confidence score
const foundStatus = (score) => ({
    text: `Adres herkend met een betrouwbaarheid van ${score}%`,
    color: Colors.successBright,
    icon: 'mdi-check-circle'
})

// Feedback shown to the user on the camera image per scan step, a function builds the message from the status data
const STATUS_MESSAGES = {
    starting: {text: 'Camera wordt gestart...', color: Colors.grey40, icon: 'mdi-camera'},
    moving: {text: 'Beweging, houd het pakket stil', color: Colors.warningBright, icon: 'mdi-motion'},
    noMovement: {text: 'Geen beweging in beeld', color: Colors.grey40, icon: 'mdi-pause-circle'},
    noAddressRetry: {text: 'Geen adres gevonden, verschuif of draai het pakket', color: Colors.errorBright, icon: 'mdi-alert-circle'},
    alreadyScanned: ({score}) => foundStatus(score),
    blurry: {text: 'Beeld is niet scherp genoeg', color: Colors.warningBright, icon: 'mdi-blur'},
    searching: {text: 'Adres wordt gezocht...', color: Colors.grey40, icon: 'mdi-magnify'},
    encodeFailed: {text: 'Beeld kon niet worden verwerkt', color: Colors.errorBright, icon: 'mdi-alert-circle'},
    serverBusy: {text: 'Server is bezig, opnieuw proberen...', color: Colors.errorBright, icon: 'mdi-alert-circle'},
    requestFailed: {text: 'Scannen mislukt, opnieuw proberen...', color: Colors.errorBright, icon: 'mdi-alert-circle'},
    loggedOut: {text: 'Je bent niet meer ingelogd, log opnieuw in om te scannen', color: Colors.errorBright, icon: 'mdi-account-alert'},
    cameraFailed: {text: 'Camera kon niet worden geopend, mogelijk is hij in gebruik door een andere app', color: Colors.errorBright, icon: 'mdi-camera-off'},
    found: ({score}) => foundStatus(score),
    notFound: {text: 'Geen adres gevonden', color: Colors.errorBright, icon: 'mdi-alert-circle'}
}

const OCR = ({onChange, onStartOCR, onClose}) => {
    // set state with useReducer
    const [state, setState] = useReducer((state, newState) => ({...state, ...newState}), {
        scan: true,
        videoInputDevices: [],
        deviceId: localStorage.getItem('cameraDeviceId') || null,
        focusMode: localStorage.getItem('focusMode') || 'manual',
        focusDistance: localStorage.getItem('focusDistance') ? parseInt(localStorage.getItem('focusDistance')) : 0,
        min: 0,
        max: 0,
        step: 0,
        varianceThreshold: localStorage.getItem('varianceThreshold') ? parseInt(localStorage.getItem('varianceThreshold')) : 400,
        confidenceThreshold: localStorage.getItem('confidenceThreshold') ? +(localStorage.getItem('confidenceThreshold')) : 90,
        motionThreshold: localStorage.getItem('motionThreshold') ? +(localStorage.getItem('motionThreshold')) : 20, // % of changed pixels
        foundAddresses: [],
        status: STATUS_MESSAGES.starting,
        debug: localStorage.getItem('ocrDebug') === 'true'
    })

    const timeoutRef = useRef(null)
    const mountedRef = useRef(true)
    const thumbnailCanvasRef = useRef(null)
    const previousThumbnailRef = useRef(null) // last frame, to detect motion
    const lastScanRef = useRef({thumbnail: null, sceneChanged: true, addressFound: false, score: 0, retries: 0, time: 0}) // last frame sent to the server

    const scanRef = useRef(state.scan)
    const varianceThresholdRef = useRef(state.varianceThreshold)
    const confidenceThresholdRef = useRef(state.confidenceThreshold)
    const motionThresholdRef = useRef(state.motionThreshold)
    const debugRef = useRef(state.debug)
    const stoppedRef = useRef(false) // scanning stopped for good, e.g. not logged in anymore

    useEffect(() => {
        scanRef.current = state.scan
        varianceThresholdRef.current = state.varianceThreshold
        confidenceThresholdRef.current = state.confidenceThreshold
        motionThresholdRef.current = state.motionThreshold
        debugRef.current = state.debug
    }, [state.scan, state.varianceThreshold, state.confidenceThreshold, state.motionThreshold, state.debug])

    useEffect(() => {
        const ocr = async () => {
            const video = document.getElementById('videoElement')

            if (navigator.mediaDevices.getUserMedia) {
                // open a camera first: device labels and ids are only exposed after camera permission is granted
                changeCamera(state.deviceId)
            }

            video.addEventListener('play', () => {
                setState({scan: true})

                // After a scan the loop already scheduled the next one (with a backoff after an error), don't replace it
                if (!timeoutRef.current) {
                    scheduleOCR(200)
                }
            })

            video.addEventListener('pause', () => {
                setState({scan: false})
            })
        }

        ocr()
    }, [])

    // cleanup on unmount
    useEffect(() => {
        return () => {
            mountedRef.current = false
            clearTimeout(timeoutRef.current)

            const video = document.getElementById('videoElement')

            if (video) {
                video.pause()

                const stream = video.srcObject

                if (stream) {
                    const tracks = stream.getTracks()
                    tracks.forEach((track) => {
                        track.stop()
                    })
                }
            }
        }
    }, [])

    // Only one scan loop may run at a time, so every (re)schedule replaces the pending one
    const scheduleOCR = (delay) => {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null

        if (mountedRef.current && !stoppedRef.current) {
            timeoutRef.current = setTimeout(doOCR, delay)
        }
    }

    const lastStatusRef = useRef(null)
    // debug: measured values shown below the message when the debug setting is on
    const statusHoldUntilRef = useRef(0)
    const showStatus = (key, data = {}, debug = '', hold = 0) => {
        // an important message (e.g. camera error) stays visible for a while
        if (!hold && Date.now() < statusHoldUntilRef.current) {
            return
        }
        statusHoldUntilRef.current = hold ? Date.now() + hold : 0

        const message = STATUS_MESSAGES[key]
        let status = typeof message === 'function' ? message(data) : message

        if (!status) {
            return
        }

        if (debugRef.current && debug) {
            status = {...status, debug}
        }

        // only re-render when the message changes, this runs a few times per second
        const last = lastStatusRef.current
        if (mountedRef.current && (status.text !== last?.text || status.debug !== last?.debug)) {
            lastStatusRef.current = status
            setState({status})
        }
    }

    const doOCR = async () => {
        timeoutRef.current = null

        const canvas = document.getElementById('canvas')
        const video = document.getElementById('videoElement')

        if (!canvas || !video || !mountedRef.current) {
            return
        }

        if (!video.videoWidth || video.paused) {
            showStatus('starting')
            if (scanRef.current) scheduleOCR(500)
            return
        }

        // Only send a frame to the server when the camera is steady and shows something new,
        // a parcel lying under the camera would otherwise be scanned over and over again
        const thumbnail = getThumbnail(video)
        const motion = previousThumbnailRef.current ? changedFraction(thumbnail, previousThumbnailRef.current) : 0
        const moving = motion * 100 > motionThresholdRef.current
        // exactly identical frames, nothing changed at all
        const motionless = !!previousThumbnailRef.current && motion === 0
        previousThumbnailRef.current = thumbnail

        const lastScan = lastScanRef.current

        if (!lastScan.thumbnail || moving) {
            // A frame with a lot of movement means the parcel was removed or replaced
            lastScan.sceneChanged = true
        } else if (!lastScan.addressFound && thumbnailsDiffer(thumbnail, lastScan.thumbnail)) {
            // No address found yet, a small change (parcel shifted or turned) is worth another scan.
            // After a successful scan small changes are ignored, so the same label is not scanned twice
            lastScan.sceneChanged = true
        }

        // Retry an unchanged scene a few times when no address was found, the next frame may be sharper
        const retry = !lastScan.addressFound && lastScan.retries < 3 && Date.now() - lastScan.time > 1500

        if (moving || motionless || (!lastScan.sceneChanged && !retry)) {
            const motionDebug = `Beweging ${(motion * 100).toFixed(1)}% (max ${motionThresholdRef.current}%)`

            if (moving) {
                showStatus('moving', {}, motionDebug)
            } else if (motionless) {
                showStatus('noMovement', {}, motionDebug)
            } else {
                showStatus(lastScan.addressFound ? 'alreadyScanned' : 'noAddressRetry', {score: lastScan.score}, `${motionDebug}, pogingen ${lastScan.retries}/3`)
            }
            scheduleOCR(300)
            return
        }

        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        const ctx = canvas.getContext('2d', {willReadFrequently: true})

        if (!ctx) {
            return
        }

        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
        const gray = toGrayscale(imageData.data)

        // Check if image is sharp enough
        const sharpness = getSharpness(gray, canvas.width, canvas.height)
        const sharp = sharpness > varianceThresholdRef.current

        showStatus(sharp ? 'searching' : 'blurry', {}, `Scherpte ${Math.round(sharpness)} (min ${varianceThresholdRef.current}), ${canvas.width}x${canvas.height}`)

        if (!sharp) {
            scheduleOCR(500)
            return
        }

        adaptiveThreshold(imageData, gray)
        ctx.putImageData(imageData, 0, 0)

        const image = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.8))

        if (!image) {
            showStatus('encodeFailed')
            scheduleOCR(500)
            return
        }

        video.pause()

        onStartOCR()

        let nextDelay = 200

        try {
            const response = await fetch('/ocr', {
                method: 'POST',
                headers: {
                    'Content-Type': 'image/jpeg',
                    'Authorization': `Bearer ${sessionStorage.token || localStorage.mtoken}`
                },
                body: image
            })

            if (response.status === 401 || response.status === 403) {
                // retrying does not help, stop scanning until the modal is opened again after logging in
                stoppedRef.current = true
                showStatus('loggedOut', {}, `HTTP ${response.status}`)
                onChange({address: null, score: 0})
                return
            }

            if (!response.ok) {
                // Server is busy (503) or the request failed, retry after a randomized backoff so clients don't retry in sync
                showStatus(response.status === 503 ? 'serverBusy' : 'requestFailed', {}, `HTTP ${response.status}`)
                nextDelay = 1000 + Math.random() * 2000
                onChange({address: null, score: 0})
                return
            }

            const result = await response.json()

            if (!mountedRef.current) {
                return
            }

            const found = resultToAddress(result)

            lastScanRef.current = {
                thumbnail,
                sceneChanged: false,
                addressFound: !!found,
                score: found?.score || 0,
                retries: lastScan.sceneChanged ? 0 : lastScan.retries + 1,
                time: Date.now()
            }

            onChange(found || {address: null, score: 0})
        } catch (err) {
            console.error(err)
            showStatus('requestFailed', {}, err?.message)
            nextDelay = 1000 + Math.random() * 2000
            onChange({address: null, score: 0})
        } finally {
            if (mountedRef.current) {
                video.play()
                scheduleOCR(nextDelay)
            }
        }
    }

    function resultToAddress(result) {
        const confidenceScores = []
        const lines = []
        const boxes = []

        // filter lines and boxes based on confidence threshold
        result.lines.forEach((line, index) => {
            const score = (result.scores[index] || 0) * 100

            if (score >= confidenceThresholdRef.current) {
                confidenceScores.push(score)
                lines.push(line)
                boxes.push(result.boxes[index])
            }
        })

        const {addressLines} = pickAddressBlock(boxes, lines, {gapMultiplier: 0.5, minAddressLines: 3})

        const address = linesToAddress(addressLines)

        const score = address ? Math.round(Math.min(...confidenceScores)) : 0

        const scores = result.scores.map((score) => Math.round((score || 0) * 100))
        showStatus(address ? 'found' : 'notFound', {score}, `${lines.length}/${result.lines.length} regels boven ${confidenceThresholdRef.current}% (${scores.join(', ') || 'geen tekst'}), ${addressLines.length} adresregels`)

        return address ? {address, score} : null
    }

    // Small grayscale version of the frame, cheap to compare between frames
    function getThumbnail(video) {
        if (!thumbnailCanvasRef.current) {
            thumbnailCanvasRef.current = document.createElement('canvas')
            thumbnailCanvasRef.current.width = 160
            thumbnailCanvasRef.current.height = 120
        }

        const canvas = thumbnailCanvasRef.current
        const ctx = canvas.getContext('2d', {willReadFrequently: true})
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

        return toGrayscale(ctx.getImageData(0, 0, canvas.width, canvas.height).data)
    }

    // Fraction of the pixels that changed noticeably, small changes are camera noise
    function changedFraction(a, b) {
        let changed = 0

        for (let i = 0; i < a.length; i++) {
            if (Math.abs(a[i] - b[i]) > 25) changed++
        }

        return changed / a.length
    }

    // Scenes differ when more than 1% of the pixels changed noticeably
    function thumbnailsDiffer(a, b) {
        return changedFraction(a, b) > SCENE_CHANGE_THRESHOLD
    }

    function toGrayscale(data) {
        const gray = new Float32Array(data.length / 4)

        for (let i = 0, j = 0; i < data.length; i += 4, j++) {
            gray[j] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]
        }

        return gray
    }

    function linesToAddress(lines) {
        const postalCodeRegex = /\b\d{4}\s?[A-Z]{2}\b/

        const address = {
            name: '',
            attention: '',
            street: '',
            nr: '',
            addition: '',
            postalCode: '',
            city: '',
            country: 'NL'
        }

        // fix postal code and city merge
        lines = lines.map((line) => {
            const fixedLine = fixPostalCodeCityMerge(line)
            return fixedLine
        })

        // find lines that contains postal code and city
        const postalCodeLineIndex = lines.findIndex((line) => postalCodeRegex.test(line))
        const postalCodeLine = lines[postalCodeLineIndex]

        if (postalCodeLine) {
            const postalCodeMatch = postalCodeLine.match(postalCodeRegex)

            if (postalCodeMatch) {
                // always '1234 AB', the postal code lookup only accepts that format
                const compact = postalCodeMatch[0].replace(/\s/g, '')
                address.postalCode = `${compact.slice(0, 4)} ${compact.slice(4)}`
                address.city = postalCodeLine.slice(postalCodeMatch.index + postalCodeMatch[0].length).trim()
            }
        }

        // Line above postal code line is street and nr
        if (postalCodeLineIndex > 0) {
            const streetLine = lines[postalCodeLineIndex - 1]
            const {street, nr, addition} = splitAddress(streetLine)
            address.street = street
            address.nr = nr
            address.addition = addition

            // if no nr if found check if the steet contains a number at the end
            if (!address.nr) {
                const match = address.street.match(/\d+/)

                if (match) {
                    address.nr = match[0]
                    address.street = address.street.slice(0, match.index).trim()
                }
            }
        }
        // If there are 2 lines above the street line, the first line is the name and the second line is attention. If only one line above, it is the name.
        if (postalCodeLineIndex > 2) {
            address.attention = lines[postalCodeLineIndex - 2].trim()
            address.name = lines[postalCodeLineIndex - 3].trim()
        } else if (postalCodeLineIndex > 1) {
            address.name = lines[postalCodeLineIndex - 2].trim()
        }

        const letterToNumber = {
            O: '0',
            o: '0',
            I: '1',
            S: '5',
            B: '8',
            G: '6',
            Z: '2',
            D: '0',
            Q: '0',
            C: '0',
            L: '1',
            T: '7',
            A: '4',
            E: '3',
            H: '4',
            K: '0',
            P: '9',
            R: '0',
            U: '0',
            V: '0',
            W: '0',
            X: '0',
            Y: '0',
            g: '9',
            q: '9',
            z: '2'
        }

        // if nr contains letters replace letters in nr with numbers for common ocr mistakes
        if (address.nr) {
            address.nr = address.nr.replace(/[OIoSBGZDCELHTAKPRUVWXYgqz]/g, (letter) => letterToNumber[letter] || letter)
            // remove leading zeros
            address.nr = address.nr.replace(/^0+/, '')
        }

        if (address.street && address.nr && address.postalCode && address.city) {
            return formatAddress(address)
        }
    }

    function fixPostalCodeCityMerge(text) {
        // Match strings like "5500ADVeldhoven" or "5500AD Veldhoven" or "5500ADVELDHOVEN"
        const match = text.match(/^(\d{4})\s?([A-Z]{2})\s?([A-Za-z]+)$/)

        if (match) {
            const digits = match[1] // 5500
            const letters = match[2] // AD
            const city = match[3] || '' // Veldhoven
            return `${digits} ${letters} ${city}`.trim()
        }

        return text
    }

    // Apply adaptive thresholding (mean of 15x15 neighborhood), uses an integral image so each pixel costs O(1)
    function adaptiveThreshold(imageData, gray) {
        const {data, width, height} = imageData

        const integral = new Float64Array((width + 1) * (height + 1))

        for (let y = 0; y < height; y++) {
            let rowSum = 0
            for (let x = 0; x < width; x++) {
                rowSum += gray[y * width + x]
                integral[(y + 1) * (width + 1) + x + 1] = integral[y * (width + 1) + x + 1] + rowSum
            }
        }

        // neighborhood size
        const size = 15
        const half = Math.floor(size / 2)

        for (let y = 0; y < height; y++) {
            const y1 = Math.max(0, y - half)
            const y2 = Math.min(height - 1, y + half) + 1

            for (let x = 0; x < width; x++) {
                const x1 = Math.max(0, x - half)
                const x2 = Math.min(width - 1, x + half) + 1

                const sum = integral[y2 * (width + 1) + x2] - integral[y1 * (width + 1) + x2] - integral[y2 * (width + 1) + x1] + integral[y1 * (width + 1) + x1]
                const mean = sum / ((x2 - x1) * (y2 - y1))

                // Threshold based on neighborhood mean minus a bias (e.g. 10)
                const val = gray[y * width + x] > mean - 10 ? 255 : 0
                const idx = (y * width + x) * 4
                data[idx] = data[idx + 1] = data[idx + 2] = val
                // alpha stays the same
            }
        }
    }

    // Variance of the Laplacian, low variance means a blurry image
    function getSharpness(gray, width, height) {
        let sum = 0
        let sumSquares = 0

        for (let y = 1; y < height - 1; y++) {
            for (let x = 1; x < width - 1; x++) {
                const i = y * width + x
                const val = 8 * gray[i] -
                    gray[i - width - 1] - gray[i - width] - gray[i - width + 1] -
                    gray[i - 1] - gray[i + 1] -
                    gray[i + width - 1] - gray[i + width] - gray[i + width + 1]

                sum += val
                sumSquares += val * val
            }
        }

        const count = (width - 2) * (height - 2)
        const mean = sum / count
        return sumSquares / count - mean * mean
    }

    async function changeDistance(event) {
        let focusDistance = event.target.value

        const constraints = {advanced: [{
            focusMode: state.focusMode,
            focusDistance: focusDistance
        }]}

        setState({focusDistance})
        localStorage.setItem('focusDistance', focusDistance)

        const video = document.getElementById('videoElement')
        const videoTracks = video.srcObject.getVideoTracks()
        const track = videoTracks[0]

        await track.applyConstraints(constraints)
    }

    async function openCamera(deviceId) {
        try {
            return await navigator.mediaDevices.getUserMedia({video: deviceId ? {deviceId: {exact: deviceId}} : true})
        } catch (error) {
            // saved camera is no longer available, fall back to the default camera
            if (!deviceId || (error.name !== 'OverconstrainedError' && error.name !== 'NotFoundError')) {
                throw error
            }
            return await navigator.mediaDevices.getUserMedia({video: true})
        }
    }

    async function changeCamera(deviceId) {
        const video = document.getElementById('videoElement')
        const previousDeviceId = video.srcObject?.getVideoTracks()[0]?.getSettings().deviceId

        // release the previous camera before opening another one, some devices can't open two cameras at once
        if (video.srcObject) {
            video.srcObject.getTracks().forEach((track) => track.stop())
        }

        let stream
        try {
            stream = await openCamera(deviceId)
        } catch (error) {
            console.error(error)
            showStatus('cameraFailed', {}, error.name, 5000)

            // reopen the previous camera so the user is not left without an image
            if (!previousDeviceId || previousDeviceId === deviceId) {
                // no camera at all, keep the message until another camera is chosen
                showStatus('cameraFailed', {}, error.name, Infinity)
                return
            }

            try {
                stream = await openCamera(previousDeviceId)
            } catch (error) {
                console.error(error)
                showStatus('cameraFailed', {}, error.name, Infinity)
                return
            }
        }

        if (stream.getVideoTracks()[0]?.getSettings().deviceId === deviceId) {
            // the chosen camera works, a previous camera error no longer applies
            statusHoldUntilRef.current = 0
        }

        video.srcObject = stream

        // store the camera that was actually opened
        const openedDeviceId = stream.getVideoTracks()[0]?.getSettings().deviceId || deviceId
        setState({deviceId: openedDeviceId})
        if (openedDeviceId) {
            localStorage.setItem('cameraDeviceId', openedDeviceId)
        }

        // permission is granted now, so the device list includes all cameras with their labels
        const devices = await navigator.mediaDevices.enumerateDevices()
        setState({videoInputDevices: devices.filter((device) => device.kind === 'videoinput' && device.deviceId)})

        // check camera focus
        const videoTracks = video.srcObject.getVideoTracks()
        const track = videoTracks[0]
        let capabilities = track.getCapabilities()

        // set resolution and framerate to 60 if available
        if (capabilities.frameRate) {
            const constraints = {
                width: {ideal: 640},
                height: {ideal: 480},
                frameRate: {ideal: 60}
            }

            try {
                await track.applyConstraints(constraints)
            } catch (error) {
                console.warn('Could not apply camera resolution/framerate', error)
            }
        }

        if (!('focusMode' in capabilities)) {
            alert('This camera does not support focus')
        } else {
            if (!('focusDistance' in capabilities)) {
                alert('This camera does not control focus distance')
            } else {
                let step = capabilities.focusDistance.step
                const min = capabilities.focusDistance.min
                const max = capabilities.focusDistance.max

                let focusDistance = state.focusDistance

                if (!focusDistance || focusDistance < min || focusDistance > max) {
                    focusDistance = max
                    setState({focusDistance})
                    localStorage.setItem('focusDistance', focusDistance)
                }

                const constraints = {advanced: [{
                    focusMode: state.focusMode,
                    focusDistance: focusDistance
                }]}

                try {
                    await track.applyConstraints(constraints)
                } catch (error) {
                    console.warn('Could not apply camera focus', error)
                }

                setState({min, max, step})
            }
        }
    }

    /**
     * Box format: [x1, y1, x2, y2]
     * lines[i] corresponds to boxes[i]
     */

    function height(b) {
        return b[3] - b[1]
    }

    function median(nums) {
        if (!nums.length) return 0
        const sorted = nums.slice().sort((a, b) => a - b)
        const mid = Math.floor(sorted.length / 2)
        return sorted.length % 2 === 1 ?
            sorted[mid] :
            (sorted[mid - 1] + sorted[mid]) / 2
    }

    function clusterByVerticalGap(boxes, gapMultiplier = 1.5) {
        if (!boxes.length) return []

        // Keep original index so we can map boxes <-> lines after sorting
        const indexed = boxes.map((b, i) => ({b, i}))
        indexed.sort((a, b) => a.b[1] - b.b[1])

        const medHeight = median(indexed.map((x) => height(x.b)))
        const gapThreshold = medHeight * gapMultiplier

        const groups = [[indexed[0]]]

        for (let k = 1; k < indexed.length; k++) {
            const curr = indexed[k]
            const lastGroup = groups[groups.length - 1]
            const prev = lastGroup[lastGroup.length - 1]

            const gap = curr.b[1] - prev.b[3] // curr.y1 - prev.y2

            if (gap <= gapThreshold) {
                lastGroup.push(curr)
            } else {
                groups.push([curr])
            }
        }

        return groups // array of groups of {b, i}
    }

    /**
     * Picks the largest vertical block as "address" and filters both boxes and lines.
     *
     * @param {number[][]} boxes - array of [x1,y1,x2,y2]
     * @param {string[]} lines - same length as boxes, lines[i] matches boxes[i]
     * @param {object} [opts]
     * @param {number} [opts.gapMultiplier=1.5] - threshold = medianHeight * gapMultiplier
     * @param {number} [opts.minAddressLines=2] - require at least this many lines to be considered address
     */
    function pickAddressBlock(boxes, lines, opts = {}) {
        const {
            gapMultiplier = 1.5,
            minAddressLines = 2
        } = opts

        if (!Array.isArray(boxes) || !Array.isArray(lines) || boxes.length !== lines.length) {
            throw new Error('boxes and lines must be arrays of the same length')
        }
        if (boxes.length === 0) {
            return {
                addressBoxes: [],
                addressLines: [],
                nonAddressBoxes: [],
                nonAddressLines: [],
                groups: []
            }
        }

        const groups = clusterByVerticalGap(boxes, gapMultiplier)

        // Choose the largest group
        let bestIdx = 0
        for (let i = 1; i < groups.length; i++) {
            if (groups[i].length > groups[bestIdx].length) bestIdx = i
        }

        // If largest group is too small, treat as "no address found"
        const bestGroup = groups[bestIdx] || []
        const addressIdxs =
        bestGroup.length >= minAddressLines ? bestGroup.map((x) => x.i) : []

        const addressSet = new Set(addressIdxs)

        const addressBoxes = []
        const addressLines = []
        const nonAddressBoxes = []
        const nonAddressLines = []

        for (let i = 0; i < boxes.length; i++) {
            if (addressSet.has(i)) {
                addressBoxes.push(boxes[i])
                addressLines.push(lines[i])
            } else {
                nonAddressBoxes.push(boxes[i])
                nonAddressLines.push(lines[i])
            }
        }

        // Optional: return groups in a friendlier shape (indices + boxes + text)
        const prettyGroups = groups.map((g) => ({
            indices: g.map((x) => x.i),
            boxes: g.map((x) => x.b),
            lines: g.map((x) => lines[x.i])
        }))

        return {
            addressBoxes,
            addressLines,
            nonAddressBoxes,
            nonAddressLines,
            groups: prettyGroups
        }
    }

    return (
        <Card shadow style={{display: 'flex', flexDirection: 'column', background: Colors.backgroundNeutral, width: 'calc(50vw - 420px)', maxWidth: 664, minWidth: 320, padding: 12}}>
            {/* no title: inside the parcel modal a title would be registered as the modal title */}
            <ModalHeader onClose={onClose} style={{marginBottom: 12}}>
                {/* before the header spacer, so the camera settings stay left and the close button right */}
                <Row style={{order: -1, alignItems: 'center', gap: formSpacing.tight}}>
                    <Select
                        id='select-camera'
                        style={{width: 250}}
                        value={state.deviceId}
                        onChange={(event) => changeCamera(event.target.value)}
                        options={state.videoInputDevices.filter((device) => {
                            return device.kind === 'videoinput'
                        }).map((device) => {
                            return {value: device.deviceId, title: device.label || `Camera ${device.deviceId}`}
                        })}
                    />
                    <Popover
                        placement='left'
                        popoverStyle={{padding: 12}}
                        noClose
                        content={
                            <>
                                <Row style={{alignItems: 'center'}}>
                                    <P style={{width: 130}}>
                                        Focus mode
                                    </P>
                                    <Select
                                        id='select-mode'
                                        style={{width: 130}}
                                        value={state.focusMode}
                                        onChange={(event) => {
                                            setState({focusMode: event.target.value})
                                            localStorage.setItem('focusMode', event.target.value)

                                            // apply focus mode
                                            const video = document.getElementById('videoElement')
                                            const videoTracks = video.srcObject.getVideoTracks()
                                            const track = videoTracks[0]
                                            const constraints = {advanced: [{
                                                focusMode: event.target.value,
                                                focusDistance: state.focusDistance
                                            }]}

                                            track.applyConstraints(constraints)
                                        }}
                                    >
                                        <option value='manual'>Handmatig</option>
                                        <option value='continuous'>Automatisch</option>
                                    </Select>
                                </Row>

                                {state.focusMode === 'manual' &&

                                    <Row style={{alignItems: 'center', height: 40}}>
                                        <P style={{width: 130}}>
                                            Focus afstand
                                        </P>

                                        <input
                                            type='range'
                                            id='distance'
                                            min={state.min}
                                            max={state.max}
                                            value={state.focusDistance}
                                            step={state.step}
                                            onChange={(event) => changeDistance(event)}
                                        />

                                        <P style={{marginLeft: 10}}>{state.focusDistance}</P>
                                    </Row>
                                }

                                <Row style={{alignItems: 'center', height: 40}}>
                                    <P style={{width: 130}}>
                                        Scherpte
                                    </P>
                                    <input
                                        type='range'
                                        min={200}
                                        max={1000}
                                        value={state.varianceThreshold}
                                        step={10}
                                        onChange={(event) => {
                                            setState({varianceThreshold: parseInt(event.target.value)})
                                            localStorage.setItem('varianceThreshold', parseInt(event.target.value))
                                        }}
                                    />

                                    <P style={{marginLeft: 10}}>{state.varianceThreshold}</P>
                                </Row>

                                <Row style={{alignItems: 'center', height: 40}}>
                                    <P style={{width: 130}}>
                                        Betrouwbaarheid
                                    </P>
                                    <input
                                        type='range'
                                        min={70}
                                        max={99.9}
                                        value={state.confidenceThreshold}
                                        step={0.1}
                                        onChange={(event) => {
                                            setState({confidenceThreshold: +(event.target.value)})
                                            localStorage.setItem('confidenceThreshold', +(event.target.value))
                                        }}
                                    />

                                    <P style={{marginLeft: 10}}>{state.confidenceThreshold.toFixed(1)}</P>
                                </Row>

                                <Row style={{alignItems: 'center', height: 40}}>
                                    <P style={{width: 130}}>
                                        Beweging
                                    </P>
                                    <input
                                        type='range'
                                        min={1}
                                        max={50}
                                        value={state.motionThreshold}
                                        step={1}
                                        onChange={(event) => {
                                            setState({motionThreshold: parseInt(event.target.value)})
                                            localStorage.setItem('motionThreshold', parseInt(event.target.value))
                                        }}
                                    />

                                    <P style={{marginLeft: 10}}>{state.motionThreshold}%</P>
                                </Row>

                                <Toggle
                                    label='Debug'
                                    info='Toon de gemeten waarden in de melding op het camerabeeld'
                                    checked={state.debug}
                                    onChange={(event) => {
                                        setState({debug: event.target.checked})
                                        localStorage.setItem('ocrDebug', event.target.checked)
                                    }}
                                    style={{marginTop: 8}}
                                />
                            </>
                        }
                    >
                        <IconButton>
                            <i className='mdi mdi-cog' />
                        </IconButton>
                    </Popover>
                </Row>
            </ModalHeader>

            <div style={{position: 'relative'}}>
                <video autoPlay={true} id='videoElement' style={{width: '100%'}} />
                <canvas id='canvas' style={{position: 'absolute', width: '100%', top: 0, left: 0, zIndex: state.scan ? -1 : 9}} />

                {state.status &&
                    <div
                        style={{
                            position: 'absolute', left: 8, right: 8, bottom: 12, zIndex: 10,
                            display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 4,
                            background: 'rgba(0, 0, 0, 0.65)', color: 'white', borderLeft: `4px solid ${state.status.color}`
                        }}
                    >
                        <i className={`mdi ${state.status.icon}`} style={{color: state.status.color, fontSize: 18}} />
                        <div style={{display: 'flex', flexDirection: 'column'}}>
                            <span>{state.status.text}</span>
                            {state.debug && state.status.debug &&
                                <span style={{fontSize: 12, opacity: 0.8}}>{state.status.debug}</span>
                            }
                        </div>
                    </div>
                }
            </div>

        </Card>

    )
}

OCR.displayName = 'OCR'

export default OCR
